import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';

/* ============================================================================
   LIGHTBOX COMPONENT
   
   Professional image viewer with:
   - Full-resolution display
   - Zoom support
   - Keyboard navigation (arrows, escape)
   - Swipe on mobile
   - Focus trapping
   - Background scroll lock
   - Screen reader labels
   - Scroll position restoration
   ============================================================================ */

const Lightbox = ({ 
  images, 
  currentIndex, 
  onClose, 
  onNext, 
  onPrev,
  isOpen 
}) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const lightboxRef = useRef(null);
  const previousActiveElement = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement;
      document.body.style.overflow = 'hidden';
      lightboxRef.current?.focus();
    } else {
      document.body.style.overflow = '';
      previousActiveElement.current?.focus();
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    switch (e.key) {
      case 'Escape':
        onClose();
        break;
      case 'ArrowRight':
        onNext();
        break;
      case 'ArrowLeft':
        onPrev();
        break;
      default:
        break;
    }
  }, [onClose, onNext, onPrev]);

  // Touch/swipe handling
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        onNext();
      } else {
        onPrev();
      }
    }
  };

  if (!isOpen || !images || images.length === 0) return null;

  const currentImage = images[currentIndex];

  return createPortal(
    <div
      ref={lightboxRef}
      role="dialog"
      aria-modal="true"
      aria-label={`Image viewer: ${currentImage?.title || 'Collection image'}`}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(0, 0, 0, 0.95)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      data-testid="lightbox"
    >
      {/* Close button */}
      <button
        onClick={onClose}
        aria-label="Close image viewer"
        style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          width: '48px',
          height: '48px',
          background: 'rgba(255,255,255,0.1)',
          border: 'none',
          borderRadius: '50%',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10
        }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>

      {/* Navigation - Previous */}
      {images.length > 1 && (
        <button
          onClick={onPrev}
          aria-label="Previous image"
          style={{
            position: 'absolute',
            left: '20px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '48px',
            height: '48px',
            background: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '50%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <path d="m15 18-6-6 6-6"/>
          </svg>
        </button>
      )}

      {/* Navigation - Next */}
      {images.length > 1 && (
        <button
          onClick={onNext}
          aria-label="Next image"
          style={{
            position: 'absolute',
            right: '20px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '48px',
            height: '48px',
            background: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '50%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <path d="m9 18 6-6-6-6"/>
          </svg>
        </button>
      )}

      {/* Image container */}
      <div style={{
        maxWidth: '90vw',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        <img
          src={currentImage?.src}
          alt={currentImage?.title || 'Collection image'}
          onClick={() => setIsZoomed(!isZoomed)}
          style={{
            maxWidth: '100%',
            maxHeight: isZoomed ? 'none' : '70vh',
            objectFit: 'contain',
            cursor: isZoomed ? 'zoom-out' : 'zoom-in',
            transition: 'transform 0.2s ease'
          }}
        />

        {/* Caption */}
        <div style={{
          marginTop: '20px',
          textAlign: 'center',
          maxWidth: '600px',
          padding: '0 20px'
        }}>
          <h3 style={{
            margin: '0 0 8px',
            fontFamily: "'Source Sans 3', sans-serif",
            fontSize: '16px',
            fontWeight: 700,
            color: 'white'
          }}>
            {currentImage?.title}
          </h3>
          {currentImage?.caption && (
            <p style={{
              margin: 0,
              fontFamily: "'Source Sans 3', sans-serif",
              fontSize: '14px',
              color: 'rgba(255,255,255,0.7)',
              lineHeight: 1.5
            }}>
              {currentImage.caption}
            </p>
          )}
          {currentImage?.category && (
            <p style={{
              margin: '8px 0 0',
              fontFamily: "'Source Sans 3', sans-serif",
              fontSize: '12px',
              fontWeight: 600,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: '#C9A962'
            }}>
              {currentImage.category}
            </p>
          )}
        </div>

        {/* Counter */}
        {images.length > 1 && (
          <p style={{
            marginTop: '16px',
            fontFamily: "'Source Sans 3', sans-serif",
            fontSize: '13px',
            color: 'rgba(255,255,255,0.5)'
          }}>
            {currentIndex + 1} of {images.length}
          </p>
        )}
      </div>
    </div>,
    document.body
  );
};

export default Lightbox;
